import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import { canAccessRoute, getAllowedRoutes, isRouteAllowed, ROLE_ACCESS_CONFIG } from '../config/roleAccessConfig';
import { getSidebarItemForRoute, SUBMODULE_PARENTS } from '../config/sidebarRouteMap';

const CAPABILITY_ROUTE_PREFIXES = [
  { prefix: '/products', capability: 'manage_products' },
  { prefix: '/sales', capability: 'process_sales' },
  { prefix: '/warehouses', capability: 'manage_inventory' },
  { prefix: '/inventory', capability: 'manage_inventory' },
  { prefix: '/stock-transfers', capability: 'manage_inventory' },
  { prefix: '/customers', capability: 'manage_customers' },
  { prefix: '/crm', capability: 'manage_customers' },
  { prefix: '/suppliers', capability: 'manage_purchases' },
  { prefix: '/purchases', capability: 'manage_purchases' },
  { prefix: '/staff', capability: 'manage_staff' },
  { prefix: '/roles', capability: 'manage_users' },
  { prefix: '/reports', capability: 'view_reports' },
  { prefix: '/settings', capability: 'manage_settings' },
];

const CAPABILITY_ROUTE_REQUIREMENTS = [
  {
    path: '/sales',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'create'],
      ['process_sales', 'report'],
    ],
  },
  {
    path: '/sales/pos',
    permissions: [
      ['process_sales', 'create'],
      ['process_sales', 'submit'],
    ],
  },
  {
    path: '/sales/invoice/new',
    permissions: [
      ['process_sales', 'create'],
      ['process_sales', 'submit'],
    ],
  },
  {
    path: '/sales/invoice/:id/edit',
    permissions: [['process_sales', 'edit']],
  },
  {
    path: '/sales/history',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'report'],
    ],
  },
  {
    path: '/sales/invoice/:id',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'report'],
    ],
  },
  {
    path: '/sales/pos-invoice/:id',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'report'],
      ['process_sales', 'print'],
    ],
  },
  {
    path: '/sales/pos-opening-entries',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'report'],
    ],
  },
  {
    path: '/sales/pos-opening-entries/:name',
    permissions: [
      ['process_sales', 'view'],
      ['process_sales', 'report'],
    ],
  },
];

const normalizePath = (path) => (path || '').replace(/\/$/, '');

const routeMatchesPattern = (routePath, pattern) => {
  const normalizedRoute = normalizePath(routePath);
  const normalizedPattern = normalizePath(pattern);

  if (normalizedRoute === normalizedPattern) {
    return true;
  }

  if (!normalizedPattern.includes(':')) {
    return false;
  }

  const routeParts = normalizedRoute.split('/').filter(Boolean);
  const patternParts = normalizedPattern.split('/').filter(Boolean);

  if (routeParts.length !== patternParts.length) {
    return false;
  }

  return patternParts.every((part, index) => (
    part.startsWith(':') || part === routeParts[index]
  ));
};

/**
 * Hook to check role-based access
 * @returns {Object} Access control utilities
 */
export const useRoleAccess = () => {
  const user = useAppSelector((state) => state.auth.user);
  const modulePermissionState = useAppSelector((state) => state.modulePermission?.modulePermissions || {});
  const capabilityPermissions = useMemo(() => {
    const permissions = user?.business_capabilities?.permissions;
    return permissions && typeof permissions === 'object' ? permissions : {};
  }, [user]);
  const userModulePermissions = useMemo(() => {
    const perms = user?.module_permissions;
    return perms && typeof perms === 'object' ? perms : {};
  }, [user]);
  const docTypePerms = useMemo(() => {
    const perms = user?.doctype_permissions;
    return perms && typeof perms === 'object' ? perms : {};
  }, [user]);
  const roles = useMemo(() => {
    // Get roles from user object
    // Roles can be in user.roles or user.role (singular)
    if (!user) return [];

    // Normalize to plain role-name strings. Roles may arrive as strings or as
    // objects ({ role } / { name } / { role_name }); every access lookup below
    // keys on the string name, so an un-normalized object shape silently denies every page.
    const toName = (r) => (typeof r === 'string' ? r : (r?.role || r?.name || r?.role_name || ''));

    if (Array.isArray(user.roles)) {
      return user.roles.map(toName).filter((r) => r && r !== 'All');
    }

    // Check if role is a string (single role)
    if (typeof user.role === 'string' && user.role !== 'All') {
      return [user.role];
    }

    return [];
  }, [user]);

  const hasLoadedPermissions = useMemo(() => {
    return (
      Object.keys(docTypePerms).length > 0 ||
      Object.keys(userModulePermissions).length > 0 ||
      Object.keys(capabilityPermissions).length > 0
    );
  }, [docTypePerms, userModulePermissions, capabilityPermissions]);

  /**
   * Check if user has access to specific DocTypes
   * @param {Array<{doctype: string, action: string}>} requiredDocTypes
   * @returns {boolean}
   */
  const hasDocTypeAccess = useMemo(() => {
    return (requiredDocTypes) => {
      if (!requiredDocTypes || !Array.isArray(requiredDocTypes) || requiredDocTypes.length === 0) {
        return true;
      }
      if (!roles || roles.length === 0) {
        return false;
      }
      if (roles.includes('Administrator') || roles.includes('System Manager')) {
        return true;
      }
      if (Object.keys(docTypePerms).length === 0) {
         return true; // Fallback will happen outside
      }

      return requiredDocTypes.every(req => {
        const { doctype, action } = req;
        const permsForDocType = docTypePerms[doctype];
        if (!permsForDocType) return false;

        const actions = action.split('/');
        return actions.some(act => permsForDocType[act] === true);
      });
    };
  }, [docTypePerms, roles]);

  /**
   * Check if current user can access a specific route
   * @param {string} routePath - The route path to check
   * @param {Array} requiredDocTypes - Optional array of required doctypes
   * @returns {boolean}
   */
  const hasAccess = useMemo(() => {
    return (routePath, requiredDocTypes = null) => {
      if (!roles || roles.length === 0) {
        return false;
      }

      // Dashboard and Permission Denied remain visible to authenticated users
      if (routePath === '/dashboard' || routePath === '/permission-denied') {
        return true;
      }

      // Check required DocTypes first (if any). Treated as ADDITIVE (grant-only),
      // consistent with module/capability checks below — a doctype permission that
      // grants the route allows access outright; one that does NOT grant it falls
      // through to the role-config check rather than hard-denying.
      if (requiredDocTypes && requiredDocTypes.length > 0) {
        if (Object.keys(docTypePerms).length > 0) {
          // Backend surfaced a doctype-permission map: if it grants this route, allow.
          if (hasDocTypeAccess(requiredDocTypes)) return true;
        }
      }

      // Administrator and System Manager roles automatically bypass fine-grained restrictions
      if (roles.includes('Administrator') || roles.includes('System Manager')) {
        return true;
      }

      // Check sidebar-item-level permissions first
      // If the route maps to a known sidebar item, require at least 'read' permission
      const sidebarItem = getSidebarItemForRoute(routePath);
      if (sidebarItem) {
        const itemName = sidebarItem.item;
        const parentName = sidebarItem.parent;

        const hasEffectivePerm = (key) => {
          if (!key) return false;
          const userPerm = userModulePermissions[key];
          const hasRead = userPerm?.read === true;
          const hasCreate = userPerm?.create === true;
          const hasRolePerm = roles.some((role) => {
            const perms = modulePermissionState[role];
            return perms?.[key]?.read === true;
          });
          return hasRead || hasCreate || hasRolePerm;
        };

        const hasItemPerm = hasEffectivePerm(itemName);
        const hasParentPerm = parentName ? hasEffectivePerm(parentName) : false;
        const hasPermGranted = hasItemPerm || hasParentPerm;

        // Module permissions are treated as ADDITIVE (grant-only): if they grant
        // this sidebar item (or its parent), access is allowed outright.
        if (hasPermGranted) {
          return true;
        }

        // Not granted by module permissions. We do NOT hard-deny here, because:
        //  1. Some sidebar items (e.g. "Audit Logs") have no DocType→module mapping
        //     in the backend, so they can NEVER appear in custom_module_permissions —
        //     hard-denying would block them for every non-admin role.
        //  2. A freshly-created user may have a role whose module permissions cover
        //     only part of what the role is allowed via ROLE_ACCESS_CONFIG / capabilities.
        // Instead, fall through to the capability and role-config checks below, which
        // are the authoritative role→route maps. Module permissions can only ADD
        // access, never revoke access that the role configuration grants.
      }

      // If capability permissions are available, use them as an ADDITIVE (grant-only)
      // source of access — exactly like module permissions above. A capability that
      // grants the route allows access outright; a capability that does NOT grant the
      // route must fall through to the role-config check below, never hard-deny.
      // (A freshly-created user may have capabilities that cover only part of what
      //  their role grants via ROLE_ACCESS_CONFIG — short-circuiting here would wrongly
      //  show "Access Restricted".)
      const hasCapabilityPermissions = Object.keys(capabilityPermissions).length > 0;
      if (hasCapabilityPermissions) {
        const specificRequirement = CAPABILITY_ROUTE_REQUIREMENTS.find(({ path }) =>
          routeMatchesPattern(routePath, path)
        );

        if (specificRequirement) {
          const granted = specificRequirement.permissions.some(
            ([capability, action]) => capabilityPermissions[`${capability}:${action}`] === true
          );
          if (granted) return true;
        } else {
          const matched = CAPABILITY_ROUTE_PREFIXES.find(({ prefix }) =>
            routePath === prefix || routePath.startsWith(`${prefix}/`)
          );

          if (matched) {
            const canView =
              capabilityPermissions[`${matched.capability}:view`] === true ||
              capabilityPermissions[`${matched.capability}:report`] === true;
            if (canView) return true;
          }
        }
      }

      if (!roles || roles.length === 0) {
        return false;
      }

      if (!hasLoadedPermissions && canAccessRoute(routePath, roles)) {
        return true;
      }

      // Final safety-net for assigned-but-unconfigured roles.
      //
      // A user can be assigned a role that the frontend's STATIC maps know nothing
      // about — e.g. an admin-created custom POS role, or a built-in role like
      // "System User" — none of which appear in ROLE_ACCESS_CONFIG. If that role
      // ALSO surfaced no module permissions, no doctype permissions and no
      // capabilities (because it has no custom_module_permissions / docperms on the
      // backend), then every grant-only check above fails and canAccessRoute() denies
      // everything, leaving the user stuck on "Access Restricted" despite clearly
      // having a role assigned.
      //
      // In that specific situation the frontend simply has NO data to gate on, so it
      // must defer route visibility to the backend rather than hard-deny. This only
      // triggers when ALL of the following hold, so it cannot widen access for users
      // who do have recognizable permissions:
      //   1. none of the user's roles exist in ROLE_ACCESS_CONFIG, AND
      //   2. no permissions (module, doctype, capability) have been loaded.
      const hasAnyConfiguredRole = roles.some((role) => role !== 'All' && ROLE_ACCESS_CONFIG[role]);

      if (
        !hasAnyConfiguredRole &&
        !hasLoadedPermissions
      ) {
        return true;
      }

      return false;
    };
  }, [roles, capabilityPermissions, userModulePermissions, modulePermissionState, docTypePerms, hasDocTypeAccess, hasLoadedPermissions]);

  const hasModuleAccess = useMemo(() => {
    return (moduleKey, requiredPerm = 'read') => {
      if (!moduleKey || !roles.length) return false;
      if (roles.includes('Administrator') || roles.includes('System Manager')) return true;

      // Check user-level aggregated module permissions first
      const userPerms = userModulePermissions[moduleKey];
      if (userPerms?.[requiredPerm] === true) return true;

      // Also check parent module permission (parent grants access to submodules)
      const parentKey = SUBMODULE_PARENTS[moduleKey];
      if (parentKey) {
        const parentPerms = userModulePermissions[parentKey];
        if (parentPerms?.[requiredPerm] === true) return true;
      }

      // Fallback to role-level module permissions
      const hasModule = roles.some((role) => {
        const perms = modulePermissionState[role];
        return perms?.[moduleKey]?.[requiredPerm] === true;
      });
      if (hasModule) return true;

      // Also fallback to role-level parent permission
      if (parentKey) {
        return roles.some((role) => {
          const perms = modulePermissionState[role];
          return perms?.[parentKey]?.[requiredPerm] === true;
        });
      }

      return false;
    };
  }, [roles, modulePermissionState, userModulePermissions]);

  /**
   * Get all allowed routes for current user
   * @returns {string[]}
   */
  const allowedRoutes = useMemo(() => {
    return getAllowedRoutes(roles);
  }, [roles]);

  /**
   * Check if a route matches any allowed route pattern
   * @param {string} routePath - The route path to check
   * @returns {boolean}
   */
  const isAllowed = useMemo(() => {
    return (routePath) => {
      return isRouteAllowed(routePath, allowedRoutes);
    };
  }, [allowedRoutes]);

  /**
   * Filter routes based on user's role access
   * @param {Array} routes - Array of route objects
   * @returns {Array} Filtered routes
   */
  const filterRoutes = useMemo(() => {
    return (routes) => {
      if (!routes || routes.length === 0) {
        return [];
      }

      return routes
        .map((route) => {
          // Check if main route is accessible
          const mainRouteAccessible = hasAccess(route.path, route.requiredDocTypes);
          const filteredPageChildren = Array.isArray(route.pageChildren)
            ? route.pageChildren.filter((child) => hasAccess(child.path, child.requiredDocTypes))
            : route.pageChildren;
          
          // If route has children, filter them first
          if (route.children && route.children.length > 0) {
            // Filter children to only include accessible ones (keep hidden routes for access checking)
            const filteredChildren = route.children.filter((child) => {
              // Keep hidden routes (they're accessed via links, but still need access check)
              if (child.hideFromMenu) {
                return hasAccess(child.path, child.requiredDocTypes);
              }
              return hasAccess(child.path, child.requiredDocTypes);
            });
            
            // Check if there are any visible (non-hidden) accessible children
            const hasVisibleAccessibleChildren = filteredChildren.some(
              (child) => !child.hideFromMenu
            );
            const hasAccessiblePageChildren =
              Array.isArray(filteredPageChildren) && filteredPageChildren.length > 0;
            
            // Only include route if:
            // 1. Main route is accessible, OR
            // 2. Has at least one visible accessible child, OR
            // 3. Has at least one accessible page child
            if (mainRouteAccessible || hasVisibleAccessibleChildren || hasAccessiblePageChildren) {
              return {
                ...route,
                children: filteredChildren,
                pageChildren: filteredPageChildren,
              };
            }
            
            // No accessible main route and no visible accessible children - hide this route
            return null;
          }
          
          // Route without children - only include if accessible
          return mainRouteAccessible
            ? {
                ...route,
                pageChildren: filteredPageChildren,
              }
            : null;
        })
        .filter((route) => route !== null); // Remove null routes (filtered out)
    };
  }, [hasAccess, userModulePermissions]);

  /**
   * Check if user has any of the specified roles
   * @param {string[]} requiredRoles - Roles to check
   * @returns {boolean}
   */
  const hasRole = useMemo(() => {
    return (requiredRoles) => {
      if (!requiredRoles || requiredRoles.length === 0) {
        return true; // No role requirement means accessible
      }
      return requiredRoles.some((role) => roles.includes(role));
    };
  }, [roles]);

  /**
   * Check if user has all of the specified roles
   * @param {string[]} requiredRoles - Roles to check
   * @returns {boolean}
   */
  const hasAllRoles = useMemo(() => {
    return (requiredRoles) => {
      if (!requiredRoles || requiredRoles.length === 0) {
        return true;
      }
      return requiredRoles.every((role) => roles.includes(role));
    };
  }, [roles]);

  return {
    roles,
    hasAccess,
    docTypePerms,
    hasDocTypeAccess,
    allowedRoutes,
    isAllowed,
    filterRoutes,
    hasRole,
    hasAllRoles,
    isAdmin: roles.includes('Administrator') || roles.includes('System Manager'),
    hasModuleAccess,
    userModulePermissions,
  };
};

export default useRoleAccess;

